import type {
  KioskDefaultPrice,
  KioskProduct,
  KioskProductClass,
  KioskProductClassSummary,
  KioskProductStockStatus,
} from "@/kiosk/types/kiosk";

export const formatKioskPrice = (price: KioskDefaultPrice | null): string => {
  if (!price) {
    return "Unavailable";
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: price.currency || "USD",
  }).format((price.unit_amount ?? 0) / 100);
};

export const buildKioskCartProduct = (
  productClass: KioskProductClass,
  product: KioskProduct,
  stockStatus?: KioskProductStockStatus | null,
): Record<string, unknown> => ({
  id: product.id,
  name: product.name,
  description: product.description,
  images: product.images,
  sku: product.sku,
  default_price: product.default_price,
  productClassId: productClass.id,
  product_class: {
    id: productClass.id,
    name: productClass.name,
    images: productClass.images,
  },
  hasInventory: stockStatus?.hasInventory ?? false,
  stockCount: stockStatus?.stockCount ?? 0,
  lowStockThreshold: stockStatus?.lowStockThreshold ?? 0,
  canOversell: stockStatus?.canOversell === true,
  inventoryLowerBound:
    stockStatus?.inventoryLowerBound === null ? null : (stockStatus?.inventoryLowerBound ?? 0),
  skipRealtimeListeners: true,
});

const LOOPBACK_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]"]);

const isLoopbackHostname = (hostname: string): boolean => {
  const normalizedHostname = hostname.trim().toLowerCase();
  return LOOPBACK_HOSTNAMES.has(normalizedHostname) || normalizedHostname.endsWith(".localhost");
};

const getCurrentHostname = (): string => {
  if (typeof window === "undefined") {
    return "";
  }

  return window.location.hostname || "";
};

const resolveReplacementHostname = (currentHostname: string): string => {
  return isLoopbackHostname(currentHostname) ? "" : currentHostname.trim();
};

export const normalizeKioskImageSrc = (
  src: string,
  currentHostname = getCurrentHostname(),
): string => {
  const trimmedSrc = src.trim();

  if (!trimmedSrc) {
    return trimmedSrc;
  }

  const replacementHostname = resolveReplacementHostname(currentHostname);

  try {
    const parsedUrl = new URL(trimmedSrc);

    if (!isLoopbackHostname(parsedUrl.hostname) || !replacementHostname) {
      return parsedUrl.toString();
    }

    parsedUrl.hostname = replacementHostname;
    return parsedUrl.toString();
  } catch {
    // Fall back to a string replacement for any non-standard URL values.
  }

  if (!replacementHostname) {
    return trimmedSrc;
  }

  return trimmedSrc.replace(/\blocalhost\b/g, replacementHostname);
};

export const getFirstKioskImage = (images?: string[]): string | null => {
  if (!Array.isArray(images)) {
    return null;
  }

  const image = images.find((entry) => typeof entry === "string" && entry.trim().length > 0);
  return image ? normalizeKioskImageSrc(image) : null;
};

export const resolveKioskProductImage = (
  product: { images?: string[] } | null | undefined,
  productClass: { images?: string[] } | null | undefined,
): string | null => {
  return getFirstKioskImage(product?.images) ?? getFirstKioskImage(productClass?.images) ?? null;
};

export const resolveKioskProductClassImage = (
  productClass: KioskProductClass | KioskProductClassSummary | null | undefined,
): string | null => getFirstKioskImage(productClass?.images);

export const resolveKioskProductOwnImage = (
  product: KioskProduct | null | undefined,
): string | null => getFirstKioskImage(product?.images);
